<template>
    <div>
        <BackHeader
            title="Información del proceso"
            :onBack="() => $router.push('/configuracion')"
        ></BackHeader>

        <p class="mb-6">
            Estas son las secciones que ve el alumno en la página del proceso de
            práctica. Arrastra una sección por su manija para cambiar el orden.
        </p>

        <v-row>
            <!-- Lista de secciones -->
            <v-col cols="12" md="3">
                <v-card class="pa-3">
                    <!-- La bienvenida va siempre arriba y sin manija: su orden
                         es -1 en la base y el backend rechaza cambiarlo. Se
                         muestra aunque todavia no exista la fila; se crea al
                         guardarla por primera vez. -->
                    <div
                        class="seccion fija"
                        :class="{ activa: esActiva(bienvenida) }"
                        @click="seleccionar(bienvenida)"
                    >
                        <v-icon small class="mr-2">mdi-lock-outline</v-icon>
                        <div class="texto">
                            <div class="font-weight-medium">Bienvenida</div>
                            <div class="caption">
                                {{ bienvenida.id ? "Siempre va primero" : "Sin contenido todavía" }}
                            </div>
                        </div>
                    </div>

                    <v-divider class="my-3"></v-divider>

                    <v-btn
                        block
                        elevation="0"
                        color="primary"
                        class="mb-3"
                        @click="crear"
                    >
                        <v-icon left small>mdi-plus</v-icon>
                        Nueva sección
                    </v-btn>

                    <draggable
                        v-model="secciones"
                        handle=".manija"
                        @start="recordarOrden"
                        @end="guardarOrden"
                    >
                        <div
                            v-for="seccion in secciones"
                            :key="seccion.id"
                            class="seccion"
                            :class="{ activa: esActiva(seccion) }"
                            @click="seleccionar(seccion)"
                        >
                            <v-icon small class="manija mr-2">mdi-drag</v-icon>
                            <div class="texto">
                                {{ seccion.titulo || "(sin título)" }}
                            </div>
                        </div>
                    </draggable>

                    <p
                        v-if="!secciones.length && !cargando"
                        class="caption text-center my-3"
                    >
                        Todavía no hay secciones.
                    </p>
                </v-card>
            </v-col>

            <!-- Edición de la sección elegida -->
            <v-col cols="12" md="9">
                <v-card v-if="formulario" class="pa-4">
                    <v-text-field
                        :value="nombreVisible"
                        @input="(valor) => (formulario.titulo = valor)"
                        label="Nombre de la sección"
                        rounded
                        filled
                        :disabled="editandoBienvenida"
                        :hint="
                            editandoBienvenida
                                ? 'El nombre de la bienvenida no se puede cambiar.'
                                : ''
                        "
                        persistent-hint
                        :error-messages="erroresTitulo"
                    ></v-text-field>

                    <Editor v-model="formulario.contenido" class="mb-4" />

                    <p v-if="erroresContenido.length" class="error--text caption">
                        {{ erroresContenido.join(" ") }}
                    </p>

                    <v-card-actions class="px-0">
                        <v-btn
                            elevation="0"
                            color="primary"
                            :disabled="!hayCambios || guardando"
                            @click="guardar"
                        >
                            Guardar cambios
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn
                            v-if="!editandoBienvenida"
                            text
                            color="error"
                            @click="confirmarBorrado = true"
                        >
                            Eliminar sección
                        </v-btn>
                    </v-card-actions>
                </v-card>

                <v-card v-else class="pa-8 text-center">
                    <p class="mb-0">Elige una sección de la lista para editarla.</p>
                </v-card>
            </v-col>
        </v-row>

        <ActionModal
            :active="confirmarBorrado"
            title="Eliminar sección"
            :successAction="borrar"
            :cancelAction="() => (confirmarBorrado = false)"
        >
            ¿Eliminar «{{ formulario ? formulario.titulo : "" }}»? Dejará de verse
            en la página del proceso y no se puede deshacer.
        </ActionModal>

        <ActionModal
            :active="confirmarDescarte"
            title="Cambios sin guardar"
            :successAction="descartarYCambiar"
            :cancelAction="() => ((confirmarDescarte = false), (pendiente = null))"
        >
            Tienes cambios sin guardar en «{{ nombreVisible }}».
            Si continúas se pierden.
        </ActionModal>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import BackHeader from "@/components/content/BackHeader";
import ActionModal from "@/components/ActionModal";
import Editor from "@/components/forms/Editor";
import { tablesService } from "@/services";

// El bloque de bienvenida se reconoce por su título, igual que en la vista
// pública (info_proceso.vue) y en el backend. Su orden es -1 y no entra en el
// reordenamiento.
const TITULO_BIENVENIDA = "index";

export default {
    components: { draggable, BackHeader, ActionModal, Editor },

    data: () => ({
        // Sin `id` mientras no exista la fila: el editor la muestra igual y el
        // primer guardado es el que la crea.
        bienvenida: { id: null, titulo: TITULO_BIENVENIDA, contenido: "" },
        secciones: [],
        formulario: null,
        original: null,
        ordenPrevio: [],
        cargando: true,
        guardando: false,
        confirmarBorrado: false,
        confirmarDescarte: false,
        pendiente: null,
        erroresTitulo: [],
        erroresContenido: [],
    }),

    computed: {
        editandoBienvenida() {
            return (
                this.formulario &&
                (this.formulario.titulo || "").trim().toLowerCase() ===
                    TITULO_BIENVENIDA
            );
        },

        // El titulo real de la bienvenida es "index": es lo que busca la vista
        // publica para pintarla fuera de los desplegables. Al administrador se
        // le muestra el nombre con que aparece en la lista, tanto en el campo
        // como en los avisos que la nombran.
        nombreVisible() {
            if (!this.formulario) {
                return "";
            }
            return this.editandoBienvenida ? "Bienvenida" : this.formulario.titulo;
        },

        hayCambios() {
            if (!this.formulario || !this.original) {
                return false;
            }
            return (
                this.formulario.titulo !== this.original.titulo ||
                this.formulario.contenido !== this.original.contenido
            );
        },
    },

    created() {
        this.cargar();
    },

    methods: {
        cargar(seleccionarId) {
            this.cargando = true;
            return tablesService
                .getService("infoproceso")
                .then((bloques) => {
                    const guardada = bloques.find(
                        (b) =>
                            (b.titulo || "").trim().toLowerCase() ===
                            TITULO_BIENVENIDA
                    );
                    this.bienvenida = guardada || {
                        id: null,
                        titulo: TITULO_BIENVENIDA,
                        contenido: "",
                    };
                    this.secciones = bloques.filter((b) => b !== guardada);
                    this.cargando = false;

                    const destino =
                        bloques.find((b) => b.id === seleccionarId) ||
                        this.bienvenida ||
                        this.secciones[0];
                    if (destino) {
                        this.abrir(destino);
                    }
                })
                .catch(() => {
                    this.cargando = false;
                    this.$store.dispatch(
                        "alert/error",
                        "No se pudieron cargar las secciones."
                    );
                });
        },

        esActiva(seccion) {
            if (!this.formulario) {
                return false;
            }
            // La bienvenida sin fila tiene id null, que no sirve para comparar:
            // se distingue por el titulo, igual que en el backend.
            if (seccion === this.bienvenida) {
                return this.editandoBienvenida;
            }
            return this.formulario.id === seccion.id;
        },

        seleccionar(seccion) {
            if (this.esActiva(seccion)) {
                return;
            }
            if (this.hayCambios) {
                this.pendiente = seccion;
                this.confirmarDescarte = true;
                return;
            }
            this.abrir(seccion);
        },

        abrir(seccion) {
            this.erroresTitulo = [];
            this.erroresContenido = [];
            this.formulario = {
                id: seccion.id,
                titulo: seccion.titulo,
                contenido: seccion.contenido,
            };
            // Copia aparte para saber si hay cambios sin guardar.
            this.original = {
                titulo: seccion.titulo,
                contenido: seccion.contenido,
            };
        },

        descartarYCambiar() {
            this.confirmarDescarte = false;
            if (this.pendiente) {
                this.abrir(this.pendiente);
                this.pendiente = null;
            }
        },

        guardar() {
            this.guardando = true;
            this.erroresTitulo = [];
            this.erroresContenido = [];

            // La bienvenida va siempre por su propia accion: esa crea la fila
            // si falta y la actualiza si ya esta, sin que aqui haya que saber
            // cual de los dos casos es. Su titulo y su orden los pone el
            // servidor, asi que solo viaja el contenido.
            const peticion = this.editandoBienvenida
                ? tablesService.newService("infoproceso/bienvenida", {
                      contenido: this.formulario.contenido,
                  })
                : tablesService.editService("infoproceso", `${this.formulario.id}/`, {
                      titulo: this.formulario.titulo,
                      contenido: this.formulario.contenido,
                  });

            peticion
                .then((bloque) => {
                    this.guardando = false;
                    // El contenido vuelve saneado por el backend: se adopta el
                    // suyo para que lo que se ve sea lo que quedó guardado.
                    this.aplicarEnLista(bloque);
                    this.abrir(bloque);
                    this.$store.dispatch("alert/success", "Sección guardada.");
                })
                .catch((error) => {
                    this.guardando = false;
                    this.mostrarErrores(error);
                });
        },

        crear() {
            const ultimo = this.secciones.length
                ? Math.max.apply(
                      null,
                      this.secciones.map((s) => s.orden)
                  )
                : 0;

            tablesService
                .newService("infoproceso", {
                    titulo: "Nueva sección",
                    contenido: "",
                    orden: ultimo + 1,
                })
                .then((bloque) => {
                    this.secciones.push(bloque);
                    this.abrir(bloque);
                })
                .catch(() => {
                    this.$store.dispatch(
                        "alert/error",
                        "No se pudo crear la sección."
                    );
                });
        },

        borrar() {
            const id = this.formulario.id;
            if (!id) {
                return;
            }
            this.confirmarBorrado = false;
            tablesService
                .deleteService("infoproceso", `${id}/`)
                .then(() => {
                    this.secciones = this.secciones.filter((s) => s.id !== id);
                    this.formulario = null;
                    this.original = null;
                    this.$store.dispatch("alert/success", "Sección eliminada.");
                })
                .catch(() => {
                    this.$store.dispatch(
                        "alert/error",
                        "No se pudo eliminar la sección."
                    );
                });
        },

        recordarOrden() {
            this.ordenPrevio = this.secciones.slice();
        },

        guardarOrden() {
            const ids = this.secciones.map((s) => s.id);
            const previos = this.ordenPrevio.map((s) => s.id);
            if (ids.join(",") === previos.join(",")) {
                return;
            }

            // Se manda la lista completa: el backend la exige para que un orden
            // calculado sobre una pantalla desactualizada falle en vez de dejar
            // huecos.
            tablesService
                .newService("infoproceso/reordenar", { orden: ids })
                .then((bloques) => {
                    this.secciones = bloques.filter(
                        (b) =>
                            (b.titulo || "").trim().toLowerCase() !==
                            TITULO_BIENVENIDA
                    );
                })
                .catch(() => {
                    // Se devuelve la lista a como estaba: dejarla en el orden
                    // nuevo haría creer que se guardó.
                    this.secciones = this.ordenPrevio;
                    this.$store.dispatch(
                        "alert/error",
                        "No se pudo guardar el orden."
                    );
                });
        },

        aplicarEnLista(bloque) {
            if ((bloque.titulo || "").trim().toLowerCase() === TITULO_BIENVENIDA) {
                this.bienvenida = bloque;
                return;
            }
            const posicion = this.secciones.findIndex((s) => s.id === bloque.id);
            if (posicion !== -1) {
                this.$set(this.secciones, posicion, bloque);
            }
        },

        mostrarErrores(error) {
            const datos = error && error.response && error.response.data;
            if (!datos) {
                this.$store.dispatch("alert/error", "No se pudo guardar.");
                return;
            }
            this.erroresTitulo = this.comoLista(datos.titulo);
            this.erroresContenido = this.comoLista(datos.contenido);
            if (!this.erroresTitulo.length && !this.erroresContenido.length) {
                this.$store.dispatch("alert/error", "No se pudo guardar.");
            }
        },

        comoLista(valor) {
            if (!valor) {
                return [];
            }
            return Array.isArray(valor) ? valor : [valor];
        },
    },
};
</script>

<style lang="scss" scoped>
.seccion {
    display: flex;
    align-items: center;
    padding: 10px 8px;
    border-radius: 6px;
    cursor: pointer;
    border: 1px solid transparent;

    &:hover {
        background-color: rgba(128, 128, 128, 0.12);
    }

    &.activa {
        border-color: var(--v-primary-base);
    }

    &.fija {
        margin-bottom: 4px;
    }

    .texto {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.manija {
    cursor: grab;
}
</style>
